import { fingerprintValue } from './quote-snapshot-diff';

/**
 * v2 (v5.47.3): the v1 fingerprint hashed VOLATILE photo state — array order
 * (photo_index), lastModified, client-generated blobId, and head/tail samples
 * of the inline base64 data. Those legitimately change across the photo
 * lifecycle (inline data is stripped after persist and re-hydrated on demand;
 * blob ids regenerate per session), so a v1 receipt written at analysis time
 * stopped matching after an ordinary save/reload — and "Reanalyse all photos"
 * could never durably satisfy the Stage 4→5 gate. v2 fingerprints only stable
 * identity (id, name, relative path, s3 key, file id, byte size) and is
 * order-insensitive. v1 receipts are reported as `legacy-source-fingerprint`
 * so callers can downgrade them to an advisory instead of a dead-stop.
 */
/**
 * v3 (2026-08-19): v2 still hashed TWO fields that are ASSIGNED mid-lifecycle
 * rather than surviving it — `s3_key` (stamped by the save-time/background
 * photo upload AFTER the receipt is written; proven live: a batch project's
 * receipt matched its photo set exactly once s3Key was nulled) and `file_id`
 * (assigned by sync import). A receipt written before first upload therefore
 * stopped matching minutes later, and the batch queue's coverage gate
 * hard-stopped every fresh project after its first save. v3 fingerprints only
 * identity that is IMMUTABLE for the life of the photo: id, name, relative
 * path, byte size. v2 receipts are re-verified through a compatibility
 * comparison (match as-is OR with the arrival-assigned fields nulled) so
 * existing receipts do not mass-invalidate.
 */
export const PHOTO_SOURCE_FINGERPRINT_VERSION = 3;
export const V2_PHOTO_SOURCE_FINGERPRINT_VERSION = 2;
export const LEGACY_PHOTO_SOURCE_FINGERPRINT_VERSION = 1;

type UnknownRecord = Record<string, any>;

export interface PhotoSourceIdentity {
    photo_id: string | null;
    photo_name: string;
    photo_index: number;
    relative_path: string | null;
}

export type PhotoForensicLineageCode =
    | 'current'
    | 'no-project-photos'
    | 'missing-receipt'
    | 'missing-source-fingerprint'
    | 'legacy-source-fingerprint'
    | 'unsupported-source-fingerprint-version'
    | 'source-count-mismatch'
    | 'source-fingerprint-mismatch'
    | 'observation-count-mismatch'
    | 'observation-index-mismatch'
    | 'observation-identity-mismatch';

export interface PhotoForensicLineageAssessment {
    matches: boolean;
    code: PhotoForensicLineageCode;
    message: string;
    expectedCount: number;
    observationCount: number;
    currentFingerprint: string | null;
    receiptFingerprint: string | null;
}

function record(value: unknown): UnknownRecord | null {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? value as UnknownRecord
        : null;
}

export function photoSourceIdentity(photo: unknown, photoIndex: number): PhotoSourceIdentity {
    const source = record(photo);
    return {
        photo_id: source?.id != null ? String(source.id) : null,
        photo_name: String(source?.originalName || source?.name || `Photo ${photoIndex + 1}`),
        photo_index: photoIndex,
        relative_path: String(
            source?.relativePath
            || source?.quoteMakerSourcePath
            || source?.sourceRelativePath
            || source?.path
            || '',
        ) || null,
    };
}

/**
 * Deterministic identity receipt for the current project-photo SET.
 * Metadata-only (no photo bytes) and restricted to STABLE identity: fields
 * that survive the ordinary photo lifecycle (persist → strip inline data →
 * reload → re-hydrate → reorder for display). Volatile state — array order,
 * lastModified, per-session blob ids, inline base64 samples — is deliberately
 * excluded: hashing it made receipts unmatchable minutes after they were
 * written (the v5.46.90–v5.47.2 "EVIDENCE BLOCKED" dead-end). Entries are
 * sorted canonically so display reorders do not change set identity;
 * reorder detection lives in the per-observation identity checks instead.
 */
export function photoForensicSourceFingerprint(photos: unknown = []): string {
    const source = Array.isArray(photos) ? photos : [];
    const identities = source.map((photo) => {
        const value = record(photo);
        return {
            photo_id: value?.id != null ? String(value.id) : null,
            photo_name: String(value?.originalName || value?.name || ''),
            relative_path: String(
                value?.relativePath
                || value?.quoteMakerSourcePath
                || value?.sourceRelativePath
                || value?.path
                || '',
            ) || null,
            size: Number.isFinite(Number(value?.size)) ? Number(value?.size) : null,
        };
    });
    identities.sort((a, b) => {
        const keyA = `${a.photo_id ?? ''} ${a.photo_name} ${a.relative_path ?? ''}`;
        const keyB = `${b.photo_id ?? ''} ${b.photo_name} ${b.relative_path ?? ''}`;
        return keyA < keyB ? -1 : keyA > keyB ? 1 : 0;
    });
    return fingerprintValue(identities);
}


/**
 * The exact v2 fingerprint shape, kept ONLY so v2 receipts can be re-verified
 * without mass-invalidation. `nullArrivalFields` computes the variant a
 * receipt written BEFORE the photo upload would carry (s3_key/file_id null).
 */
export function photoForensicSourceFingerprintV2(
    photos: unknown = [],
    { nullArrivalFields = false }: { nullArrivalFields?: boolean } = {},
): string {
    const source = Array.isArray(photos) ? photos : [];
    const identities = source.map((photo) => {
        const value = record(photo);
        return {
            photo_id: value?.id != null ? String(value.id) : null,
            photo_name: String(value?.originalName || value?.name || ''),
            relative_path: String(
                value?.relativePath
                || value?.quoteMakerSourcePath
                || value?.sourceRelativePath
                || value?.path
                || '',
            ) || null,
            s3_key: !nullArrivalFields && value?.s3Key != null ? String(value.s3Key) : null,
            file_id: !nullArrivalFields && value?.fileId != null ? String(value.fileId) : null,
            size: Number.isFinite(Number(value?.size)) ? Number(value?.size) : null,
        };
    });
    identities.sort((a, b) => {
        const keyA = `${a.photo_id ?? ''} ${a.photo_name} ${a.relative_path ?? ''} ${a.s3_key ?? ''}`;
        const keyB = `${b.photo_id ?? ''} ${b.photo_name} ${b.relative_path ?? ''} ${b.s3_key ?? ''}`;
        return keyA < keyB ? -1 : keyA > keyB ? 1 : 0;
    });
    return fingerprintValue(identities);
}

function result(
    matches: boolean,
    code: PhotoForensicLineageCode,
    message: string,
    expectedCount: number,
    observationCount: number,
    currentFingerprint: string | null,
    receiptFingerprint: string | null,
): PhotoForensicLineageAssessment {
    return {
        matches,
        code,
        message,
        expectedCount,
        observationCount,
        currentFingerprint,
        receiptFingerprint,
    };
}

/**
 * Proves that a persisted forensic receipt belongs to the current, complete,
 * ordered project-photo set. Counts alone are deliberately insufficient: a
 * same-size set can contain replaced photos or the same photos in a new order.
 */
export function assessPhotoForensicLineage({
    photos,
    photoAnalysis,
}: {
    photos?: unknown;
    photoAnalysis?: unknown;
} = {}): PhotoForensicLineageAssessment {
    const projectPhotos = Array.isArray(photos) ? photos : [];
    const expectedCount = projectPhotos.length;
    if (expectedCount === 0) {
        return result(true, 'no-project-photos', 'The project has no photos to match.', 0, 0, null, null);
    }

    const receipt = record(photoAnalysis);
    if (!receipt) {
        return result(false, 'missing-receipt', 'No persisted photo-forensic receipt exists.', expectedCount, 0, photoForensicSourceFingerprint(projectPhotos), null);
    }

    const observations = Array.isArray(receipt.observations) ? receipt.observations : [];
    const currentFingerprint = photoForensicSourceFingerprint(projectPhotos);
    const attempt = record(receipt.analysis_attempt);
    const rootFingerprint = typeof receipt.source_fingerprint === 'string'
        ? receipt.source_fingerprint.trim()
        : '';
    const attemptFingerprint = typeof attempt?.source_fingerprint === 'string'
        ? attempt.source_fingerprint.trim()
        : '';
    const receiptFingerprint = rootFingerprint || attemptFingerprint || null;

    if (!rootFingerprint || !attemptFingerprint) {
        return result(
            false,
            'missing-source-fingerprint',
            'The persisted photo-forensic receipt does not contain complete source-fingerprint lineage.',
            expectedCount,
            observations.length,
            currentFingerprint,
            receiptFingerprint,
        );
    }

    const rootVersion = Number(receipt.source_fingerprint_version);
    const attemptVersion = Number(attempt?.source_fingerprint_version);
    if (rootVersion === LEGACY_PHOTO_SOURCE_FINGERPRINT_VERSION
        && attemptVersion === LEGACY_PHOTO_SOURCE_FINGERPRINT_VERSION) {
        // v1 fingerprints hashed volatile state and cannot be re-verified
        // against the live photo set without false mismatches. Callers should
        // treat this as "reanalyse when convenient", not a dead-stop.
        return result(
            false,
            'legacy-source-fingerprint',
            'The persisted photo-forensic receipt predates the stable v2 source fingerprint and cannot be re-verified in place.',
            expectedCount,
            observations.length,
            currentFingerprint,
            receiptFingerprint,
        );
    }
    // v2 receipts stay verifiable through the compatibility comparison below
    // — they hashed s3_key/file_id, which are ASSIGNED by uploads/sync after
    // the receipt is written, so they need a two-candidate match instead of
    // an invalidation.
    const isV2Receipt = rootVersion === V2_PHOTO_SOURCE_FINGERPRINT_VERSION
        && attemptVersion === V2_PHOTO_SOURCE_FINGERPRINT_VERSION;
    if (!isV2Receipt
        && (rootVersion !== PHOTO_SOURCE_FINGERPRINT_VERSION
            || attemptVersion !== PHOTO_SOURCE_FINGERPRINT_VERSION)) {
        return result(
            false,
            'unsupported-source-fingerprint-version',
            'The persisted photo-forensic receipt uses an unsupported or missing source-fingerprint version.',
            expectedCount,
            observations.length,
            currentFingerprint,
            receiptFingerprint,
        );
    }

    if (Number(attempt?.source_count) !== expectedCount) {
        return result(
            false,
            'source-count-mismatch',
            `The persisted receipt was created for ${Number(attempt?.source_count) || 0} photos, not the current ${expectedCount}.`,
            expectedCount,
            observations.length,
            currentFingerprint,
            receiptFingerprint,
        );
    }

    // A v2 receipt matches when the current set hashes to it EITHER with the
    // arrival-assigned fields as they are now (keys were already present at
    // analysis time) OR with them nulled (the receipt was written before the
    // first upload — the world every fresh batch project lives in).
    const fingerprintMatchesCurrent = isV2Receipt
        ? (rootFingerprint === photoForensicSourceFingerprintV2(projectPhotos)
            || rootFingerprint === photoForensicSourceFingerprintV2(projectPhotos, { nullArrivalFields: true }))
        : rootFingerprint === currentFingerprint;
    if (rootFingerprint !== attemptFingerprint || !fingerprintMatchesCurrent) {
        return result(
            false,
            'source-fingerprint-mismatch',
            'The current ordered project-photo set does not match the persisted forensic source fingerprint.',
            expectedCount,
            observations.length,
            currentFingerprint,
            receiptFingerprint,
        );
    }

    if (observations.length !== expectedCount) {
        return result(
            false,
            'observation-count-mismatch',
            `The persisted receipt contains ${observations.length} photo identities for the current ${expectedCount} photos.`,
            expectedCount,
            observations.length,
            currentFingerprint,
            receiptFingerprint,
        );
    }

    const observationsByIndex = new Map<number, UnknownRecord>();
    for (const value of observations) {
        const observation = record(value);
        const index = observation?.photo_index;
        if (!observation || !Number.isInteger(index) || index < 0 || index >= expectedCount || observationsByIndex.has(index)) {
            return result(
                false,
                'observation-index-mismatch',
                'The persisted receipt does not contain one unique observation at every current project-photo index.',
                expectedCount,
                observations.length,
                currentFingerprint,
                receiptFingerprint,
            );
        }
        observationsByIndex.set(index, observation);
    }

    for (let index = 0; index < projectPhotos.length; index += 1) {
        const expected = photoSourceIdentity(projectPhotos[index], index);
        const observation = observationsByIndex.get(index);
        const observedId = observation?.photo_id != null ? String(observation.photo_id) : null;
        const observedName = observation?.photo_name != null ? String(observation.photo_name) : '';
        const observedSourceId = observation?.source_id != null ? String(observation.source_id) : '';
        if (!observation
            || observedId !== expected.photo_id
            || observedName !== expected.photo_name
            || observedSourceId !== `photo-${index + 1}`) {
            return result(
                false,
                'observation-identity-mismatch',
                `The persisted observation identity at photo ${index + 1} does not match the current project photo.`,
                expectedCount,
                observations.length,
                currentFingerprint,
                receiptFingerprint,
            );
        }
    }

    return result(
        true,
        'current',
        'The persisted photo-forensic receipt matches the current ordered project-photo set.',
        expectedCount,
        observations.length,
        currentFingerprint,
        receiptFingerprint,
    );
}
